import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav.js';
import { tokens } from '../theme';
import { AGE_GROUPS, DEFAULT_SEASON } from '../config/seasons';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  CircularProgress,
  Divider,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/Lock';

import {
  getOverrides,
  searchGamesForOverride,
  createOrUpdateOverride,
  deleteOverride,
  getTeams,
  verifyAdminKey,
  setAdminKey,
  clearAdminKey,
} from '../services/api';


function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatScore(home, away) {
  if (home === null && away === null) return '—';
  const h = home !== null ? home : '?';
  const a = away !== null ? away : '?';
  return `${h} – ${a}`;
}

// ---------- Admin key gate ----------
// The key is checked by the server and held only for this browser session. The old
// gate compared against a constant compiled into the JS bundle, so it kept nobody
// out — and the write endpoints behind it had no authentication at all.
function PasswordGate({ onAuth }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setChecking(true);
    setError('');
    const result = await verifyAdminKey(password);
    setChecking(false);
    if (result.ok) {
      setAdminKey(password);
      sessionStorage.setItem('adminAuthed', 'true');
      onAuth();
    } else {
      setError(result.reason || 'That key was not accepted.');
      setPassword('');
    }
  };

  return (
    <Container maxWidth="xs" sx={{ mt: 12 }}>
      <Card>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <LockIcon sx={{ fontSize: 48, color: tokens.navy }} />
            <Typography variant="h6">Admin Access</Typography>
            {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Admin key"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                autoFocus
                fullWidth
                disabled={checking}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={checking || !password}
                sx={{ backgroundColor: tokens.navy }}
              >
                {checking ? 'Checking…' : 'Sign in'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

// ---------- Override Form Dialog ----------
function OverrideDialog({ game, open, onClose, onSaved }) {
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (game && open) {
      setHomeScore(game.override_home_score !== null && game.override_home_score !== undefined ? String(game.override_home_score) : '');
      setAwayScore(game.override_away_score !== null && game.override_away_score !== undefined ? String(game.override_away_score) : '');
      setError(null);
    }
  }, [game, open]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const home = homeScore.trim() === '' ? null : parseInt(homeScore, 10);
      const away = awayScore.trim() === '' ? null : parseInt(awayScore, 10);
      await createOrUpdateOverride(game.match_id, home, away);
      onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to save override');
    } finally {
      setSaving(false);
    }
  };

  if (!game) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {game.has_override ? 'Edit Override' : 'Create Override'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {formatDate(game.game_date)} · {game.age_group} · {game.home_team} vs {game.away_team}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Upstream score: <strong>{formatScore(game.actual_home_score, game.actual_away_score)}</strong>
          {(game.actual_home_score !== null || game.actual_away_score !== null) && (
            <span> ({game.home_team} {game.actual_home_score} – {game.away_team} {game.actual_away_score})</span>
          )}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Leave a score field blank to show "TBD" for that score.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label={`${game.home_team} score`}
            value={homeScore}
            onChange={(e) => setHomeScore(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="TBD"
            inputProps={{ inputMode: 'numeric' }}
            fullWidth
          />
          <TextField
            label={`${game.away_team} score`}
            value={awayScore}
            onChange={(e) => setAwayScore(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="TBD"
            inputProps={{ inputMode: 'numeric' }}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ backgroundColor: tokens.navy }}>
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Override'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Delete Confirm Dialog ----------
function DeleteConfirmDialog({ override, open, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Reset state each time the dialog opens so stale spinner/error never carries over
  useEffect(() => {
    if (open) {
      setDeleting(false);
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await deleteOverride(override.match_id);
      onDeleted();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to delete override');
      setDeleting(false);
    }
  };

  if (!override) return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Remove Override?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Remove the score override for <strong>{override.home_team} vs {override.away_team}</strong> ({formatDate(override.game_date)})?
          The upstream score ({formatScore(override.actual_home_score, override.actual_away_score)}) will be shown again.
        </DialogContentText>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>Cancel</Button>
        <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
          {deleting ? <CircularProgress size={20} color="inherit" /> : 'Remove Override'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Current Overrides Section ----------
function CurrentOverridesSection({ overrides, loading, error, onDelete }) {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>Current Overrides</Typography>
      {loading && <CircularProgress size={24} />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && overrides.length === 0 && (
        <Alert severity="info">No overrides currently active.</Alert>
      )}
      {!loading && overrides.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Age</strong></TableCell>
                <TableCell><strong>Home Team</strong></TableCell>
                <TableCell><strong>Away Team</strong></TableCell>
                <TableCell align="center"><strong>Upstream Score</strong></TableCell>
                <TableCell align="center"><strong>Override Score</strong></TableCell>
                <TableCell><strong>Created</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {overrides.map((row) => (
                <TableRow key={row.match_id} hover>
                  <TableCell>{formatDate(row.game_date)}</TableCell>
                  <TableCell>{row.age_group}</TableCell>
                  <TableCell>{row.home_team}</TableCell>
                  <TableCell>{row.away_team}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="What the scraper recorded">
                      <span style={{ color: '#666' }}>
                        {formatScore(row.actual_home_score, row.actual_away_score)}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="What users see">
                      <Chip
                        size="small"
                        label={
                          row.override_home_score === null && row.override_away_score === null
                            ? 'TBD'
                            : formatScore(row.override_home_score, row.override_away_score)
                        }
                        color="primary"
                        sx={{ backgroundColor: tokens.navy }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>{formatDate(row.override_created_at)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="Remove override">
                      <IconButton size="small" color="error" onClick={() => onDelete(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

// ---------- Game Search Section ----------
function GameSearchSection({ onOverrideSaved }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [ageGroups, setAgeGroups] = useState([]);
  const [selectedTeamName, setSelectedTeamName] = useState('');
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [overrideGame, setOverrideGame] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Reload team list whenever age group selection changes — mirrors Schedule page behavior
  useEffect(() => {
    const loadTeams = async () => {
      setTeamsLoading(true);
      setSelectedTeamName('');
      try {
        const data = await getTeams(ageGroups.length ? ageGroups : AGE_GROUPS, undefined, DEFAULT_SEASON);
        setTeams(Array.isArray(data) ? data : []);
      } catch (err) {
        setTeams([]);
      } finally {
        setTeamsLoading(false);
      }
    };
    loadTeams();
  }, [ageGroups.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async () => {
    setLoading(true);
    setError(null);
    setSearched(true);
    try {
      const data = await searchGamesForOverride(
        dateFrom || null,
        dateTo || null,
        ageGroups,
        selectedTeamName || null,
      );
      setResults(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  const openOverrideDialog = (game) => {
    setOverrideGame(game);
    setDialogOpen(true);
  };

  const handleOverrideSaved = () => {
    handleSearch();
    onOverrideSaved();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Create / Edit Override</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search for a game, then click Override to set a custom score. Leave scores blank to show "TBD".
      </Typography>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <TextField
          label="From date"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 160 }}
        />
        <TextField
          label="To date"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
          sx={{ minWidth: 160 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Age Groups</InputLabel>
          <Select
            multiple
            value={ageGroups}
            onChange={(e) => setAgeGroups(e.target.value)}
            input={<OutlinedInput label="Age Groups" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((v) => <Chip key={v} label={v} size="small" />)}
              </Box>
            )}
          >
            {AGE_GROUPS.map((ag) => (
              <MenuItem key={ag} value={ag}>{ag}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Team</InputLabel>
          <Select
            value={selectedTeamName}
            onChange={(e) => setSelectedTeamName(e.target.value)}
            input={<OutlinedInput label="Team" />}
            disabled={teamsLoading}
            MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
          >
            <MenuItem value="">All Teams</MenuItem>
            {teams.map((team) => (
              <MenuItem key={team.name} value={team.name}>{team.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={loading}
          sx={{ backgroundColor: tokens.navy, alignSelf: 'center' }}
        >
          Search
        </Button>
      </Box>

      {/* Results */}
      {loading && <CircularProgress size={24} />}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && searched && results.length === 0 && (
        <Alert severity="info">No games found matching your filters.</Alert>
      )}
      {!loading && results.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Age</strong></TableCell>
                <TableCell><strong>Home Team</strong></TableCell>
                <TableCell><strong>Away Team</strong></TableCell>
                <TableCell align="center"><strong>Upstream Score</strong></TableCell>
                <TableCell align="center"><strong>Override</strong></TableCell>
                <TableCell align="center"><strong>Action</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((game) => (
                <TableRow key={game.match_id} hover>
                  <TableCell>{formatDate(game.game_date)}</TableCell>
                  <TableCell>{game.age_group}</TableCell>
                  <TableCell>{game.home_team}</TableCell>
                  <TableCell>{game.away_team}</TableCell>
                  <TableCell align="center" sx={{ color: '#666' }}>
                    {formatScore(game.actual_home_score, game.actual_away_score)}
                  </TableCell>
                  <TableCell align="center">
                    {game.has_override ? (
                      <Chip
                        size="small"
                        label={
                          game.override_home_score === null && game.override_away_score === null
                            ? 'TBD'
                            : formatScore(game.override_home_score, game.override_away_score)
                        }
                        color="primary"
                        sx={{ backgroundColor: tokens.navy }}
                      />
                    ) : (
                      <span style={{ color: '#aaa' }}>none</span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon />}
                      onClick={() => openOverrideDialog(game)}
                    >
                      {game.has_override ? 'Edit' : 'Override'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <OverrideDialog
        game={overrideGame}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={handleOverrideSaved}
      />
    </Box>
  );
}

// ---------- Main Admin Page ----------
export default function Admin() {
  const [authed, setAuthed] = useState(sessionStorage.getItem('adminAuthed') === 'true');
  const [overrides, setOverrides] = useState([]);
  const [loadingOverrides, setLoadingOverrides] = useState(false);
  const [overridesError, setOverridesError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const fetchOverrides = useCallback(async () => {
    setLoadingOverrides(true);
    setOverridesError(null);
    try {
      const data = await getOverrides();
      setOverrides(data);
    } catch (err) {
      setOverridesError(err?.response?.data?.detail || 'Failed to load overrides');
    } finally {
      setLoadingOverrides(false);
    }
  }, []);

  useEffect(() => {
    if (authed) fetchOverrides();
  }, [authed, fetchOverrides]);

  if (!authed) {
    return <PasswordGate onAuth={() => setAuthed(true)} />;
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <AdminNav />
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700} color="#0D203D">
          Score Overrides
        </Typography>
        <Button
          variant="text"
          size="small"
          color="inherit"
          onClick={() => { clearAdminKey(); sessionStorage.removeItem('adminAuthed'); setAuthed(false); }}
          sx={{ color: '#666' }}
        >
          Log out
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Overrides replace the upstream score shown to users. Deleting an override restores the original upstream data.
        Cache is cleared automatically on save/delete.
      </Alert>

      <CurrentOverridesSection
        overrides={overrides}
        loading={loadingOverrides}
        error={overridesError}
        onDelete={(row) => { setDeleteTarget(row); setDeleteDialogOpen(true); }}
      />

      <Divider sx={{ my: 4 }} />

      <GameSearchSection onOverrideSaved={fetchOverrides} />

      <DeleteConfirmDialog
        override={deleteTarget}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={fetchOverrides}
      />
    </Container>
  );
}
